import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { haptic } from '@/lib/haptics';

interface CountValidation {
  productId: string;
  productName: string;
  severity: 'ERROR' | 'WARNING';
  type: 'EXCEEDS_STOCK' | 'INCOMPLETE' | 'UNCHANGED' | 'ZERO';
  message: string;
  value: number;
  originalValue?: number;
  maxAllowed?: number;
}

interface ConfirmCountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type: 'MORNING' | 'EVENING';
  totalProducts: number;
  warnings: CountValidation[];
  isAdmin?: boolean;
}

export function ConfirmCountModal({
  isOpen,
  onClose,
  onConfirm,
  type,
  totalProducts,
  warnings,
  isAdmin
}: ConfirmCountModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleConfirm = () => {
    haptic.medium();
    onConfirm();
    setAcknowledged(false); // Reset for next time
  };

  const handleClose = () => {
    haptic.light();
    onClose();
    setAcknowledged(false); // Reset
  };

  // Separate errors and warnings
  const errors = warnings.filter(w => w.severity === 'ERROR');
  const softWarnings = warnings.filter(w => w.severity === 'WARNING');
  const hasErrors = errors.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Confirm ${type === 'MORNING' ? 'Morning' : 'Evening'} Count`}
      size="lg"
    >
      <div className="space-y-4">
        <div className="bg-dark-lighter rounded-lg p-4">
          <p className="text-gray-300">
            You are about to {isAdmin ? 'save' : 'submit'} <strong className="text-gold">{totalProducts}</strong> product count{totalProducts !== 1 ? 's' : ''}.
            {isAdmin ? (
              type === 'EVENING' && (
                <span className="block mt-2 text-yellow-500 flex items-center gap-2">
                  <span>⚠️</span>
                  <span>This will update stock levels.</span>
                </span>
              )
            ) : (
              <span className="block mt-2 text-blue-400 flex items-center gap-2">
                <span>📋</span>
                <span>Your count will be submitted for admin approval.</span>
              </span>
            )}
          </p>
        </div>

        {/* ERRORS SECTION - Red, blocks save */}
        {errors.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <h3 className="text-red-500 font-semibold mb-3 flex items-center gap-2">
              <span>🚫</span>
              <span>{errors.length} Critical Error{errors.length > 1 ? 's' : ''} - Cannot Save</span>
            </h3>

            <div className="max-h-48 overflow-y-auto space-y-2">
              {errors.map((error, i) => (
                <div key={i} className="text-sm text-gray-300 bg-red-500/5 rounded p-2 border border-red-500/20">
                  <div className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <div className="flex-1">
                      <strong>{error.productName}</strong>
                      <span className="text-red-400"> — {error.message}</span>
                      {error.type === 'EXCEEDS_STOCK' && (
                        <div className="text-xs text-gray-400 mt-1">
                          Entered: {error.value}g | Max allowed: {error.maxAllowed}g
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 text-sm text-red-400 bg-red-500/5 rounded p-2 border border-red-500/20">
              ⚠️ Please go back and fix these errors before saving.
            </div>
          </div>
        )}

        {/* WARNINGS SECTION - Yellow, can acknowledge */}
        {softWarnings.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <h3 className="text-yellow-500 font-semibold mb-3 flex items-center gap-2">
              <span>⚠️</span>
              <span>{softWarnings.length} Warning{softWarnings.length > 1 ? 's' : ''} Found</span>
            </h3>

            <div className="max-h-48 overflow-y-auto space-y-2">
              {softWarnings.map((warning, i) => (
                <div key={i} className="text-sm text-gray-300 flex items-start gap-2">
                  <span className="text-yellow-500 mt-0.5">•</span>
                  <div>
                    <strong>{warning.productName}</strong>
                    <span className="text-gray-400"> — {warning.message}</span>
                  </div>
                </div>
              ))}
            </div>

            {!hasErrors && (
              <label className="flex items-start gap-3 mt-4 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => { haptic.light(); setAcknowledged(e.target.checked); }}
                  className="w-4 h-4 mt-0.5 accent-yellow-500"
                />
                <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                  I have verified all flagged items are correct
                </span>
              </label>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            className="flex-1"
          >
            Go Back
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={hasErrors || (softWarnings.length > 0 && !acknowledged)}
            className="flex-1"
          >
            {hasErrors
              ? 'Fix Errors First'
              : softWarnings.length > 0
                ? isAdmin ? 'Confirm & Save' : 'Confirm & Submit'
                : isAdmin ? 'Save Count' : 'Submit for Review'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
